import { forwardRef, memo, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { RX1_PALETTE } from '@ui/Components/Sdr/canvasRendering';
import type { AlertSeverity, BaselinesPayload, RunningStats, SweepDrawConfig } from '@ui/Types/Gnss';

import AllocationOverlayBand, {
    type SpectrumWaterfallAllocationOverlay,
    type SpectrumWaterfallAllocationSegment,
} from './components/AllocationOverlayBand';
import useAllocationViewportTracking from './hooks/useAllocationViewportTracking';
import useSpectrumTooltip from './hooks/useSpectrumTooltip';
import useSweepRenderer from './hooks/useSweepRenderer';
import {
    getSweepEffectiveLeftPad,
    SPECTRUM_WATERFALL_ALLOCATION_BAND_HEIGHT_PX,
    SPECTRUM_WATERFALL_AXIS_HEIGHT_PX,
} from './math/layout';
import { exportSpectrumWaterfallImage, exportSweepRowsCsv } from './utils/exportCanvas';

import type { Ref, RefObject } from 'react';

export type { SpectrumWaterfallAllocationOverlay, SpectrumWaterfallAllocationSegment };

export interface SpectrumWaterfallCanvasProps {
    readonly alertMarkersRef?: RefObject<{ db: number; freqMHz: number; severity?: AlertSeverity; ts: number }[]>;
    readonly allocationOverlay?: SpectrumWaterfallAllocationOverlay | null;
    readonly baselineChannel?: 'rx1' | 'rx2';
    readonly baselineColor?: string;
    // Preferred: full baseline payload with channel selector
    readonly baselineRef?: RefObject<BaselinesPayload | null> | null;
    // Legacy baseline as rows; retained for compatibility
    readonly baselineRowsRef?: RefObject<number[][]> | null;
    readonly captureRecentTile?: () => Promise<void> | void;
    readonly colorOffsetDb?: number;
    readonly cursorRef?: RefObject<{ xContent: number | null } | null>;
    readonly drawConfigRef?: RefObject<SweepDrawConfig | null>;
    readonly fillWaterfallViewport?: boolean;
    readonly frameCounterRef: RefObject<number>;
    readonly height?: number;
    readonly highThresholdPct?: number; // percentage (0-100)
    readonly layoutKey?: unknown;
    readonly leftPadPx?: number;
    readonly maxRows?: number;
    readonly mediumThresholdPct?: number; // percentage (0-100)
    readonly palette?: { average: string; latest: string; peak: string };
    readonly pxPerMHz?: number;
    readonly pxPerRow?: number;
    readonly rightPadPx?: number;
    readonly scaleFromParentHeight?: boolean;
    readonly secondaryRowsRef?: RefObject<number[][]> | null;
    readonly secondaryStatsRef?: RefObject<RunningStats | null>;
    readonly showAlertThresholds?: boolean;
    readonly showAverage?: boolean;
    readonly showBaseline?: boolean;
    readonly showLatest?: boolean;
    readonly showPeak?: boolean;
    readonly snapshotTileHeightPx?: number;
    readonly spectrumCssH?: number;
    // Optional running stats for primary and secondary channels
    readonly statsRef?: RefObject<RunningStats | null>;
    readonly sweepRowsRef?: RefObject<number[][]> | null;
    readonly thresholdColors?: { high: string; medium: string };
    readonly totalMHz?: number;
    readonly waterfallCssHeightOverride?: number;
}

export interface SpectrumWaterfallCanvasHandles {
    axisCanvas: HTMLCanvasElement | null;
    container: HTMLDivElement | null;
    contentBox: HTMLDivElement | null;
    exportCsv: (filename?: string) => void;
    exportImage: (filename?: string) => Promise<void>;
    spectrumCanvas: HTMLCanvasElement | null;
    waterfallCanvas: HTMLCanvasElement | null;
}

function SpectrumWaterfallCanvas(
    {
        alertMarkersRef,
        allocationOverlay = null,
        baselineChannel = 'rx1',
        baselineColor,
        baselineRef = null,
        baselineRowsRef = null,
        captureRecentTile,
        colorOffsetDb = 0,
        cursorRef,
        drawConfigRef,
        fillWaterfallViewport = false,
        frameCounterRef,
        height = 400,
        highThresholdPct,
        layoutKey,
        leftPadPx = 24,
        maxRows = 500,
        mediumThresholdPct,
        palette = RX1_PALETTE,
        pxPerMHz = 1.5,
        pxPerRow = 20,
        rightPadPx = 24,
        scaleFromParentHeight = false,
        secondaryRowsRef = null,
        secondaryStatsRef,
        showAlertThresholds,
        showAverage = true,
        showBaseline,
        showLatest = true,
        showPeak = true,
        snapshotTileHeightPx,
        spectrumCssH = 200,
        statsRef,
        sweepRowsRef,
        thresholdColors,
        totalMHz = 6000,
        waterfallCssHeightOverride,
    }: Readonly<SpectrumWaterfallCanvasProps>,
    ref: Ref<SpectrumWaterfallCanvasHandles>,
) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const contentBoxRef = useRef<HTMLDivElement | null>(null);
    const axisCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const spectrumCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const waterfallCanvasRef = useRef<HTMLCanvasElement | null>(null);

    const canvasMetricsRef = useRef<{
        axis: { cssH: number; cssW: number; devH: number; devW: number } | null;
        spectrum: { cssH: number; cssW: number; devH: number; devW: number } | null;
        waterfall: { cssH: number; cssW: number; devH: number; devW: number } | null;
    }>({ axis: null, spectrum: null, waterfall: null });

    const [devicePixelRatio] = useState<number>(() => window.devicePixelRatio || 1);
    const [measuredParentHeightPx, setMeasuredParentHeightPx] = useState<number | null>(null);
    const measureRafIdRef = useRef<number | null>(null);

    const { allocationLayoutPxPerMHz, allocationViewport } = useAllocationViewportTracking({
        allocationOverlayEnabled: Boolean(allocationOverlay),
        containerRef,
        leftPadPx,
        pxPerMHz,
        totalMHz,
    });

    useEffect(() => {
        if (!scaleFromParentHeight) {
            return;
        }

        const container = containerRef.current;
        const parent = container?.parentElement;
        if (!container || !parent) {
            return;
        }

        const measureHeight = () => {
            measureRafIdRef.current = null;
            setMeasuredParentHeightPx(Math.max(180, Math.floor(parent.clientHeight)));
        };

        const scheduleMeasure = () => {
            if (measureRafIdRef.current !== null) {
                return;
            }
            measureRafIdRef.current = requestAnimationFrame(measureHeight);
        };

        scheduleMeasure();

        let observer: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(() => {
                scheduleMeasure();
            });
            observer.observe(parent);
        }

        return () => {
            if (observer) {
                observer.disconnect();
            }
            if (measureRafIdRef.current !== null) {
                cancelAnimationFrame(measureRafIdRef.current);
                measureRafIdRef.current = null;
            }
        };
    }, [scaleFromParentHeight]);

    const resolvedHeightPx: number = scaleFromParentHeight ? (measuredParentHeightPx ?? height) : height;

    const allocationBandHeight: number = allocationOverlay ? SPECTRUM_WATERFALL_ALLOCATION_BAND_HEIGHT_PX : 0;
    const resolvedCanvasHeightPx: number = Math.max(
        180,
        scaleFromParentHeight ? resolvedHeightPx - allocationBandHeight : resolvedHeightPx,
    );
    const resolvedTotalHeightPx: number = scaleFromParentHeight
        ? resolvedHeightPx
        : resolvedCanvasHeightPx + allocationBandHeight;
    const effectiveLeftPad: number = getSweepEffectiveLeftPad(leftPadPx);

    const resolvedSpectrumHeight = scaleFromParentHeight
        ? Math.max(
              110,
              Math.min(
                  Math.floor((resolvedCanvasHeightPx - (SPECTRUM_WATERFALL_AXIS_HEIGHT_PX + 18)) * 0.4),
                  resolvedCanvasHeightPx - (SPECTRUM_WATERFALL_AXIS_HEIGHT_PX + 36),
              ),
          )
        : spectrumCssH;

    useSweepRenderer({
        alertMarkersRef,
        axisCanvasRef,
        baselineChannel,
        baselineColor,
        baselineRef: baselineRef ?? undefined,
        baselineRowsRef: baselineRowsRef ?? undefined,
        canvasMetricsRef,
        captureRecentTile,
        colorOffsetDb,
        containerRef,
        contentBoxRef,
        cursorRef,
        devicePixelRatio,
        drawConfigRef,
        fillWaterfallViewport,
        frameCounterRef,
        height: resolvedCanvasHeightPx,
        highThresholdPct,
        layoutKey,
        leftPadPx,
        maxRows,
        mediumThresholdPct,
        palette,
        pxPerMHz,
        rightPadPx,
        rowHeightPx: pxPerRow,
        secondaryRowsRef: secondaryRowsRef ?? undefined,
        secondaryStatsRef,
        showAlertThresholds,
        showAverage,
        showBaseline,
        showLatest,
        showPeak,
        snapshotTileHeightPx,
        spectrumCanvasRef,
        spectrumCssH: resolvedSpectrumHeight,
        statsRef,
        sweepRowsRef,
        thresholdColors,
        totalMHz,
        waterfallCanvasRef,
        waterfallCssHeightOverride,
    });

    const tooltipElRef = useSpectrumTooltip({
        baselineChannel,
        baselineRef,
        baselineRowsRef,
        containerRef,
        cursorRef,
        drawConfigRef,
        frameCounterRef,
        leftPadPx,
        maxRows,
        pxPerMHz,
        secondaryRowsRef,
        secondaryStatsRef,
        showAverage,
        showLatest,
        showPeak,
        spectrumCanvasRef,
        statsRef,
        sweepRowsRef,
        totalMHz,
    });

    useImperativeHandle(ref, () => ({
        axisCanvas: axisCanvasRef.current,
        container: containerRef.current,
        contentBox: contentBoxRef.current,
        exportCsv: (filename) =>
            exportSweepRowsCsv({
                drawConfig: drawConfigRef?.current ?? null,
                filename,
                rows: sweepRowsRef?.current ?? [],
            }),
        exportImage: (filename) =>
            exportSpectrumWaterfallImage({
                axisCanvas: axisCanvasRef.current,
                filename,
                metrics: canvasMetricsRef.current,
                spectrumCanvas: spectrumCanvasRef.current,
                waterfallCanvas: waterfallCanvasRef.current,
            }),
        spectrumCanvas: spectrumCanvasRef.current,
        waterfallCanvas: waterfallCanvasRef.current,
    }));

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: scaleFromParentHeight ? '100%' : resolvedTotalHeightPx,
                minHeight: 0,
                minWidth: 0,
                width: '100%',
            }}
        >
            <div
                ref={containerRef}
                style={{
                    backgroundColor: '#000',
                    flex: 1,
                    minHeight: 0,
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    position: 'relative',
                    width: '100%',
                }}
            >
                <div
                    ref={contentBoxRef}
                    style={{
                        paddingTop: allocationBandHeight,
                        position: 'relative',
                    }}
                >
                    <AllocationOverlayBand
                        allocationBandHeight={allocationBandHeight}
                        allocationLayoutPxPerMHz={allocationLayoutPxPerMHz}
                        allocationOverlay={allocationOverlay}
                        allocationViewport={allocationViewport}
                        effectiveLeftPad={effectiveLeftPad}
                        totalMHz={totalMHz}
                    />

                    <canvas
                        ref={axisCanvasRef}
                        style={{
                            display: 'block',
                            height: SPECTRUM_WATERFALL_AXIS_HEIGHT_PX,
                            left: 0,
                            pointerEvents: 'none',
                            position: 'sticky',
                            top: 0,
                            zIndex: 5,
                        }}
                    />
                    <canvas
                        ref={spectrumCanvasRef}
                        style={{
                            height: resolvedSpectrumHeight,
                            left: 0,
                            position: 'absolute',
                            top: allocationBandHeight + SPECTRUM_WATERFALL_AXIS_HEIGHT_PX,
                        }}
                    />
                    <canvas
                        ref={waterfallCanvasRef}
                        style={{
                            left: 0,
                            position: 'absolute',
                            top: allocationBandHeight + SPECTRUM_WATERFALL_AXIS_HEIGHT_PX + resolvedSpectrumHeight,
                        }}
                    />
                    <div
                        ref={tooltipElRef}
                        style={{
                            background: 'rgba(0,0,0,0.8)',
                            borderRadius: '4px',
                            color: 'white',
                            display: 'none',
                            fontSize: '12px',
                            left: 0,
                            padding: '4px',
                            pointerEvents: 'none',
                            position: 'absolute',
                            top: 0,
                            zIndex: 10,
                        }}
                    />
                </div>
            </div>
        </div>
    );
}

const ForwardedSpectrumWaterfallCanvas = forwardRef<
    SpectrumWaterfallCanvasHandles,
    Readonly<SpectrumWaterfallCanvasProps>
>(SpectrumWaterfallCanvas);

ForwardedSpectrumWaterfallCanvas.displayName = 'SpectrumWaterfallCanvas';

export default memo(ForwardedSpectrumWaterfallCanvas);
